import { useState } from 'react';

import { ApiError } from '../../api/client';
import { FUNNEL_STAGE_LABELS, type DialogDto, type FunnelStage } from '../../api/types';
import { useAddNote, useContact } from '../../hooks/use-contacts';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { useToast } from '../ui/toast';
import { MemoryList } from './MemoryList';

function contactName(dialog: DialogDto): string {
  const { firstName, lastName } = dialog.contact;
  const name = [firstName, lastName].filter(Boolean).join(' ');
  return name || `VK ID ${dialog.contact.vkUserId}`;
}

function stageLabel(stage: string): string {
  return FUNNEL_STAGE_LABELS[stage as FunnelStage] ?? stage;
}

/**
 * Card of the contact for the current dialog. Fetches GET /contacts/:id for consent,
 * memory (summary/facts/objections/agreements) and recent events, plus note-taking.
 * If that call 403s — most commonly because the current user has no `contacts.read`
 * grant for this channel — the panel degrades to funnel stage + tags from the dialog
 * itself (GET /dialogs/:id) instead of showing a page-level error.
 */
export function ContactPanel({ dialog }: { dialog: DialogDto }) {
  const contactQuery = useContact(dialog.contact.id);
  const addNote = useAddNote();
  const toast = useToast();
  const [noteText, setNoteText] = useState('');
  const [addedNotes, setAddedNotes] = useState<{ id: string; text: string; createdAt: string }[]>([]);

  const forbidden = contactQuery.error instanceof ApiError && contactQuery.error.status === 403;
  const otherError = contactQuery.isError && !forbidden;

  function submitNote(e: React.FormEvent) {
    e.preventDefault();
    const text = noteText.trim();
    if (!text) return;
    addNote.mutate(
      { id: dialog.contact.id, text },
      {
        onSuccess: (res) => {
          setNoteText('');
          setAddedNotes((prev) => [{ id: res.note.id, text: res.note.text, createdAt: res.note.createdAt }, ...prev]);
        },
        onError: (err) => toast.push(err instanceof Error ? err.message : 'Не удалось добавить заметку'),
      },
    );
  }

  if (contactQuery.isLoading) {
    return (
      <aside className="flex w-72 shrink-0 flex-col gap-3 border-l border-border p-4">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </aside>
    );
  }

  const data = contactQuery.data;
  const funnelStage = data?.contact.funnelStage ?? dialog.contact.funnelStage;
  const tags = data?.contact.tags ?? dialog.contact.tags;
  const consent = data?.consents.find((c) => c.channelId === dialog.channelId) ?? null;

  return (
    <aside className="flex w-72 shrink-0 flex-col gap-4 overflow-y-auto border-l border-border p-4 text-sm">
      <div>
        <h2 className="font-semibold">{contactName(dialog)}</h2>
        <a
          className="text-xs text-primary hover:underline"
          href={`https://vk.com/id${dialog.contact.vkUserId}`}
          target="_blank"
          rel="noreferrer"
        >
          vk.com/id{dialog.contact.vkUserId}
        </a>
      </div>

      {otherError && <p className="text-xs text-red-600">Не удалось загрузить карточку контакта.</p>}
      {forbidden && <p className="text-xs text-muted-fg">Нет доступа к карточке контакта.</p>}

      <div>
        <div className="mb-1 text-xs font-medium text-muted-fg">Стадия воронки</div>
        <Badge tone="info">{stageLabel(funnelStage)}</Badge>
      </div>

      <div>
        <div className="mb-1 text-xs font-medium text-muted-fg">Теги</div>
        {tags.length === 0 ? (
          <span className="text-xs text-muted-fg">Нет тегов</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {tags.map((tag) => (
              <Badge key={tag}>{tag}</Badge>
            ))}
          </div>
        )}
      </div>

      {data && (
        <>
          <div>
            <div className="mb-1 text-xs font-medium text-muted-fg">Согласие на текущий канал</div>
            {consent ? (
              <div className="text-xs">
                <Badge tone={consent.status === 'granted' ? 'success' : 'danger'}>{consent.status}</Badge>{' '}
                <span className="text-muted-fg">
                  {consent.basis}
                  {consent.grantedAt && ` · ${new Date(consent.grantedAt).toLocaleDateString('ru-RU')}`}
                </span>
              </div>
            ) : (
              <span className="text-xs text-muted-fg">Нет данных о согласии</span>
            )}
          </div>

          <MemoryList memory={data.memory} />

          <div>
            <div className="mb-1 text-xs font-medium text-muted-fg">Недавние события</div>
            {data.recentEvents.length === 0 ? (
              <span className="text-xs text-muted-fg">Нет событий</span>
            ) : (
              <ul className="space-y-1 text-xs">
                {data.recentEvents.slice(0, 8).map((e) => (
                  <li key={e.id}>
                    {e.type} · {new Date(e.at).toLocaleString('ru-RU')}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}

      <form onSubmit={submitNote} className="space-y-2">
        <div className="text-xs font-medium text-muted-fg">Заметка</div>
        {addedNotes.length > 0 && (
          <ul className="space-y-1 text-xs text-muted-fg">
            {addedNotes.map((n) => (
              <li key={n.id}>
                {n.text} · {new Date(n.createdAt).toLocaleString('ru-RU')}
              </li>
            ))}
          </ul>
        )}
        <textarea
          className="h-16 w-full rounded border border-border bg-card p-2 text-sm"
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          placeholder="Добавить заметку…"
        />
        <Button type="submit" size="sm" disabled={!noteText.trim() || addNote.isPending}>
          Добавить
        </Button>
      </form>
    </aside>
  );
}
